import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://axiom-danismanlik.com';

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updateMetaTag('og:title', title, true);
      updateMetaTag('twitter:title', title);
    }

    // Update canonical URL
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    
    canonicalLink.setAttribute('href', canonicalUrl);

    // Update OG URL
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', ogType, true);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }

      const schemaWithUrl = {
        '@context': 'https://schema.org',
        ...schema,
        url: canonicalUrl
      };

      schemaScript.textContent = JSON.stringify(schemaWithUrl);
    }
  }, [title, description, keywords, canonical, ogType, schema, location.pathname, siteUrl]);
};

// Schema.org templates
export const createOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  logo: 'https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/723dc54c55285e324fa54e72204552e6.jpeg',
  description: 'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı - ISO standartları, süreç optimizasyonu, kalite yönetim sistemleri, işe alım, performans yönetimi ve kurumsal eğitim hizmetleri sunuyoruz.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Maslak Mahallesi, Büyükdere Caddesi',
    addressLocality: 'İstanbul',
    addressRegion: 'Kocaeli',
    addressCountry: 'TR'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+90-212-555-23',
    contactType: 'customer service',
    email: 'info@axiomkalite.com',
    availableLanguage: ['Turkish', 'English']
  },
  sameAs: [
    'https://www.linkedin.com/company/axiom',
    'https://twitter.com/axiom',
    'https://www.instagram.com/axiom',
    'https://www.facebook.com/axiom'
  ]
});

export const createWebPageSchema = (name: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  inLanguage: ['tr-TR', 'en-US'],
  isPartOf: {
    '@type': 'WebSite',
    name: 'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    url: import.meta.env.VITE_SITE_URL || 'https://example.com'
  }
});

export const createServiceSchema = (name: string, description: string) => ({
  '@type': 'Service',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: 'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı'
  },
  areaServed: {
    '@type': 'Country',
    name: 'Turkey'
  }
});

export const createFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});
